import Badge from '../ui/Badge';
import Button from '../ui/Button';

/**
 * Invoice List Component
 * Displays billing history and invoices
 */

const InvoiceList = ({ invoices }) => {
  const getStatusBadge = (status) => {
    const variants = {
      paid: 'success',
      pending: 'warning',
      failed: 'error',
      refunded: 'default',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  if (!invoices || invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <p className="text-neutral-600">No invoices yet</p>
        <p className="text-sm text-neutral-500 mt-1">Your billing history will appear here</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-neutral-200">
            <th className="text-left py-3 px-4 font-semibold text-sm text-neutral-700">Date</th>
            <th className="text-left py-3 px-4 font-semibold text-sm text-neutral-700">Description</th>
            <th className="text-left py-3 px-4 font-semibold text-sm text-neutral-700">Amount</th>
            <th className="text-left py-3 px-4 font-semibold text-sm text-neutral-700">Status</th>
            <th className="text-right py-3 px-4 font-semibold text-sm text-neutral-700">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="hover:bg-neutral-50 transition-colors">
              <td className="py-4 px-4 text-sm text-neutral-900">{formatDate(invoice.date)}</td>
              <td className="py-4 px-4">
                <div className="text-sm text-neutral-900">{invoice.description}</div>
                {invoice.invoiceNumber && (
                  <div className="text-xs text-neutral-500 mt-1">#{invoice.invoiceNumber}</div>
                )}
              </td>
              <td className="py-4 px-4 text-sm font-semibold text-neutral-900">
                {formatAmount(invoice.amount)}
              </td>
              <td className="py-4 px-4">{getStatusBadge(invoice.status)}</td>
              <td className="py-4 px-4 text-right">
                <div className="flex items-center justify-end gap-2">
                  {invoice.status === 'paid' && invoice.pdfUrl && invoice.pdfUrl !== '#' && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => window.open(invoice.pdfUrl)}>
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = invoice.pdfUrl || '#';
                          link.download = `invoice-${invoice.invoiceNumber}.pdf`;
                          link.click();
                        }}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                        Download
                      </Button>
                    </>
                  )}
                  {invoice.status === 'paid' && (!invoice.pdfUrl || invoice.pdfUrl === '#') && (
                    <span className="text-xs text-neutral-400">Paid</span>
                  )}
                  {invoice.status === 'pending' && (
                    <Button variant="primary" size="sm">
                      Pay Now
                    </Button>
                  )}
                  {invoice.status === 'failed' && (
                    <Button variant="danger" size="sm">
                      Retry Payment
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InvoiceList;
